// Settings → Audit: the account's own trail (T3). Owner and Admin only, on
// plans with team access — the tab is absent otherwise, per the settings
// shell's rules. Each entry renders as one sentence (the platform console's
// audit pattern), newest first, keyset-paged with Load more.
import { useEffect, useState } from 'react';
import {
  AccountAuditEntry, AdminUser, adminListUsers, auditExportUrl, listAccountAuditApi,
} from '../../api/admin';
import { TableCard } from '../../components/fs';
import { PlanLimitBody, planLimitOf } from '../../api/limits';
import PlanLimitBlock from '../../components/PlanLimitBlock';
import { PHONE, useMediaQuery } from '../../shared/media';

const q = (s: string | null) => `“${s ?? '—'}”`;

/**
 * One sentence per entry, exported for its test. Written for the reader who
 * was not there: names in place, no codes to cross-reference. An action this
 * build does not know renders as itself rather than vanishing — an audit
 * trail must not hide entries a newer server wrote.
 */
export function describeAccountAudit(e: AccountAuditEntry): string {
  const d = e.detail || {};
  switch (e.action) {
    case 'signin.password':
      if (d.twoFactor) return 'signed in with a password and a two-factor code';
      if (d.enrollmentRequired) return 'signed in with a password — two-factor setup still required';
      return 'signed in with a password';
    case 'signin.sso': return `signed in through ${d.provider || 'single sign-on'}`;
    case 'signin.failed': return 'failed to sign in (wrong password)';
    case 'signout': return 'signed out';
    case 'signout.others': return `signed out their other sessions${typeof d.sessions === 'number' ? ` (${d.sessions})` : ''}`;
    case 'twofactor.enable': return 'turned on two-factor authentication';
    case 'twofactor.disable': return 'turned off two-factor authentication';
    case 'twofactor.reset': return `reset two-factor authentication for ${e.target}`;
    case 'password.change': return 'changed their password';
    case 'password.reset':
      if (d.via === 'link') return `emailed ${e.target} a password reset link`;
      if (d.via === 'set') return `set a new password for ${e.target}`;
      if (d.via === 'invite') return 'accepted their invitation and set a password';
      return 'set a new password from a reset link';
    case 'user.invite': return `invited ${e.target} as ${d.role || 'a member'}`;
    case 'user.role': return `changed ${e.target} from ${d.from} to ${d.to}`;
    case 'user.active': return d.active ? `reactivated ${e.target}` : `deactivated ${e.target}`;
    case 'form.create': return d.template ? `created the form ${q(e.target)} from a template` : `created the form ${q(e.target)}`;
    case 'form.publish': return `published ${q(e.target)}`;
    case 'form.restore': return `restored the document of ${q(e.target)} from version ${d.version}`;
    case 'form.status': return `moved ${q(e.target)} from ${d.from} to ${d.to}`;
    case 'form.delete': return `deleted the form ${q(e.target)}`;
    case 'form.access': return `set ${q(e.target)} to ${d.mode} access`;
    case 'connection.create': return `added the ${d.type} connection ${q(e.target)}`;
    case 'connection.update': return `changed the ${d.type} connection ${q(e.target)}`;
    case 'connection.delete': return `removed the ${d.type} connection ${q(e.target)}`;
    case 'apikey.create': return `created the API key ${q(e.target)}`;
    case 'apikey.revoke': return `revoked ${e.target}`;
    case 'submission.delete': return `deleted submission ${e.target}`;
    case 'export.run':
      if (d.kind === 'audit-csv') return `exported the audit log as CSV${typeof d.rows === 'number' ? ` (${d.rows} entries)` : ''}`;
      return d.kind === 'zip'
        ? `exported ${d.count ?? 'the selected'} submissions as PDFs from ${q(e.target)}`
        : `exported ${String(d.kind || 'data').toUpperCase()} from ${q(e.target)}`;
    case 'storage.sweep': return `deleted the stored files of ${q(e.target)}`;
    case 'billing.checkout': return `started checkout for the ${d.plan} plan${typeof d.trialDays === 'number' && d.trialDays > 0 ? ` with ${d.trialDays} days free` : ''}`;
    case 'billing.upgrade': {
      const charged = typeof d.chargedCents === 'number' && d.chargedCents > 0 ? ` — ${(d.chargedCents / 100).toFixed(2)} charged` : '';
      return `upgraded the plan from ${d.from} to ${d.to}${charged}`;
    }
    case 'billing.downgrade': return `moved the plan from ${d.from} to ${d.to}${d.scheduled ? ' at period end' : ''}`;
    case 'billing.cancel': return `cancelled the plan${d.scheduled ? ' — Free at period end' : ' — moved to Free'}`;
    case 'billing.changed_in_stripe': {
      // Written by the webhook, as "Stripe": a plan change made in the billing
      // portal or Stripe's Dashboard, which ends the agreed price and the percent off.
      const ended = (d.discountsEnded || {}) as { agreedCents?: number | null; percent?: { percent: number } | null };
      const what = [
        typeof ended.agreedCents === 'number' ? 'the agreed price' : null,
        ended.percent ? `the ${ended.percent.percent}% off` : null,
      ].filter(Boolean).join(' and ');
      return `recorded a plan change from ${d.from} to ${d.to} made in the billing portal or Stripe’s Dashboard${what ? ` — ${what} ended` : ''}`;
    }
    case 'payment.refund': {
      // R11: a respondent's money given back from Settings → Payments.
      const cents = typeof d.amountCents === 'number' ? d.amountCents : null;
      const cur = String(d.currency || 'usd').toUpperCase();
      const amount = cents === null ? 'a payment' : `${(cents / 100).toFixed(2)} ${cur}`;
      const whom = d.respondent ? ` to ${d.respondent}` : '';
      return `refunded ${amount}${whom}${d.full ? ' in full' : ''}${e.target ? ` for ${q(e.target)}` : ''}`;
    }
    case 'sso.settings':
      if (d.newFormAccess) return `set new forms to be visible to ${d.newFormAccess === 'everyone' ? 'everyone' : 'their maker'}`;
      return `changed team sign-in settings${Array.isArray(d.changed) && d.changed.length ? ` (${(d.changed as string[]).join(', ')})` : ''}`;
    case 'policy.two_factor':
      return d.required ? 'made two-factor authentication required for everyone' : 'stopped requiring two-factor authentication';
    case 'erasure.run':
      return `erased everything for ${e.target}: ${d.submissions ?? 0} submissions, ${d.invites ?? 0} invitations, ${d.links ?? 0} links`;
    case 'retention.run':
      return `retention deleted ${d.count} submission${d.count === 1 ? '' : 's'} older than ${d.days} days from ${q(e.target)}`;
    case 'domain.add': return `added the custom domain ${e.target}`;
    case 'domain.verify': return `verified the custom domain ${e.target}`;
    case 'domain.remove': return `removed the custom domain ${e.target}`;
    case 'account.brand.update':
      if (d.logo === 'added') return 'added a logo to the account branding';
      if (d.logo === 'removed') return 'removed a logo from the account branding';
      return 'changed the account branding';
    case 'privacy.file_links': {
      const to = (d.to || {}) as { mode?: string; days?: number };
      return `set file links to ${to.mode === 'never' ? 'never ask for a sign-in'
        : to.mode === 'days' ? `ask for a sign-in after ${to.days} day${to.days === 1 ? '' : 's'}` : 'the standard lifetime'}`;
    }
    case 'privacy.ai':
      return d.submissionAccess ? 'let the AI assistant read submissions' : 'stopped the AI assistant reading submissions';
    case 'assistant.submissions': return `asked the AI assistant about the submissions of ${q(e.target)}`;
    // Form-Magic staff entering the account in support mode (074), with the
    // access they chose and the reason they gave — the actor is their email.
    case 'support.enter': {
      const how = d.access === 'read' ? 'read-only' : 'with full access';
      const ref = d.ref ? ` (${d.ref})` : '';
      return `entered the account as Form-Magic support, ${how}${d.reason ? ` — ${q(String(d.reason))}` : ''}${ref}`;
    }
    case 'support.exit': return 'left the account (Form-Magic support)';
    case 'webhook.create': return `added the webhook ${q(e.target)}`;
    case 'webhook.update':
      return `changed the webhook ${q(e.target)}${Array.isArray(d.changed) && d.changed.length ? ` (${(d.changed as string[]).join(', ')})` : ''}`;
    case 'webhook.delete': return `removed the webhook ${q(e.target)}`;
    // Review finding 103: sharing, groups, retention, edits, credentials, workflows, SSO providers.
    case 'form.share':
      return `changed who can see the form ${q(e.target)}${typeof d.grants === 'number' ? ` — ${d.grants} ${d.grants === 1 ? 'person or group' : 'people or groups'}` : ''}`;
    case 'folder.share':
      return `changed who can see the folder ${q(e.target)}${typeof d.grants === 'number' ? ` — ${d.grants} ${d.grants === 1 ? 'person or group' : 'people or groups'}` : ''}`;
    case 'group.create': return `created the group ${q(e.target)}`;
    case 'group.update':
      return d.renamedTo ? `renamed the group ${q(e.target)} to ${q(String(d.renamedTo))}` : `changed the members of the group ${q(e.target)}`;
    case 'group.delete': return `deleted the group ${q(e.target)}`;
    case 'form.retention': {
      const days = (v: unknown) => (v && typeof v === 'object' && typeof (v as { days?: unknown }).days === 'number' ? `${(v as { days: number }).days} days` : 'for ever');
      return `changed how long ${q(e.target)} keeps its submissions, from ${days(d.from)} to ${days(d.to)}`;
    }
    case 'submission.edit':
      return `edited submission ${e.target}${d.form ? ` on ${q(String(d.form))}` : ''}${typeof d.fields === 'number' ? ` — ${d.fields} ${d.fields === 1 ? 'answer' : 'answers'} changed` : ''}`;
    case 'credential.update': return `saved the account’s ${e.target} credentials`;
    case 'credential.delete': return 'removed an integration’s saved credentials';
    case 'workflow.update': return `changed the workflow of ${q(e.target)}`;
    case 'workflow.reset': return `reset the workflow of ${q(e.target)} to its connections`;
    case 'sso.provider':
      return `${d.change === 'added' ? 'added' : d.change === 'removed' ? 'removed' : 'changed'} the sign-in provider ${q(e.target)}`;
    // Form-Magic staff changing the account from their console, outside a
    // support visit. The actor is their email; `what` is the console's name
    // for the change, and one nobody has worded yet still reads as a change.
    case 'staff.change': {
      const what = STAFF_CHANGE[String(d.what)] || 'changed this account';
      return `${what}${e.target ? ` for ${e.target}` : ''}, as Form-Magic staff`;
    }
    default: return e.action;
  }
}

const STAFF_CHANGE: Record<string, string> = {
  'account.rename': 'renamed the account',
  'account.status': 'suspended or reopened the account',
  'account.plan': 'changed the plan',
  'account.billing': 'changed the plan or its price',
  'account.price': 'changed the price',
  'account.free_period': 'changed the free period',
  'account.credit': 'added or changed a credit',
  'account.billing_email': 'changed the billing email',
  'account.discount': 'changed the discount',
  'account.trial_grant': 'started a free upgrade period',
  'account.trial_update': 'changed the free upgrade period',
  'account.trial_revoke': 'ended the free upgrade period',
  'account.delete_scheduled': 'scheduled the account for deletion',
  'account.restore': 'called off the account’s deletion',
  'account.portal_link': 'made a link to the billing portal',
  'account.user_add': 'added a user',
  'user.password_reset': 'reset the password',
  'user.invite_resent': 'sent a new invitation',
};

/** The filter's vocabulary, grouped how people ask the question. */
const ACTION_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Everything' },
  { value: 'signin.password', label: 'Password sign-ins' },
  { value: 'signin.sso', label: 'SSO sign-ins' },
  { value: 'signin.failed', label: 'Failed sign-ins' },
  { value: 'signout.others', label: 'Other sessions signed out' },
  { value: 'twofactor.enable', label: 'Two-factor turned on' },
  { value: 'twofactor.disable', label: 'Two-factor turned off' },
  { value: 'twofactor.reset', label: 'Two-factor resets' },
  { value: 'password.reset', label: 'Password resets' },
  { value: 'user.invite', label: 'Invitations' },
  { value: 'user.role', label: 'Role changes' },
  { value: 'user.active', label: 'Deactivations' },
  { value: 'form.create', label: 'Forms created' },
  { value: 'form.publish', label: 'Forms published' },
  { value: 'form.restore', label: 'Documents restored from a version' },
  { value: 'form.delete', label: 'Forms deleted' },
  { value: 'form.access', label: 'Form access changes' },
  { value: 'connection.create', label: 'Connections added' },
  { value: 'connection.delete', label: 'Connections removed' },
  { value: 'apikey.create', label: 'API keys created' },
  { value: 'apikey.revoke', label: 'API keys revoked' },
  { value: 'submission.delete', label: 'Submissions deleted' },
  { value: 'export.run', label: 'Exports' },
  { value: 'storage.sweep', label: 'Storage sweeps' },
  { value: 'payment.refund', label: 'Refunds' },
  { value: 'erasure.run', label: 'Erasures' },
  { value: 'retention.run', label: 'Retention runs' },
  { value: 'domain.add', label: 'Domains added' },
  { value: 'domain.verify', label: 'Domains verified' },
  { value: 'domain.remove', label: 'Domains removed' },
  { value: 'account.brand.update', label: 'Branding changes' },
  { value: 'sso.settings', label: 'Sign-in settings' },
  { value: 'policy.two_factor', label: 'Two-factor policy' },
  { value: 'privacy.ai', label: 'AI submission access' },
  { value: 'assistant.submissions', label: 'AI assistant reads' },
  { value: 'form.share', label: 'Form sharing' },
  { value: 'folder.share', label: 'Folder sharing' },
  { value: 'submission.edit', label: 'Submission edits' },
  { value: 'form.retention', label: 'Retention changes' },
  { value: 'support.enter', label: 'Form-Magic support visits' },
  { value: 'staff.change', label: 'Changes by Form-Magic staff' },
];

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export default function AuditSettings() {
  const [entries, setEntries] = useState<AccountAuditEntry[] | null>(null);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [actorId, setActorId] = useState('');
  const [action, setAction] = useState('');
  const [error, setError] = useState('');
  const [limitHit, setLimitHit] = useState<PlanLimitBody | null>(null);
  const [busy, setBusy] = useState(false);
  const phone = useMediaQuery(PHONE);

  const filters = () => ({
    actorId: actorId ? Number(actorId) : undefined,
    action: action || undefined,
  });

  async function load(before?: number) {
    setBusy(true); setError('');
    try {
      const r = await listAccountAuditApi({ ...filters(), before });
      setEntries((cur) => (before && cur ? [...cur, ...r.entries] : r.entries));
      setNextBefore(r.nextBefore);
    } catch (e: any) {
      const limit = planLimitOf(e);
      if (limit) setLimitHit(limit);
      else setError(e.message || 'Could not load the audit trail.');
      if (!before) setEntries([]);
    } finally { setBusy(false); }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [actorId, action]);
  useEffect(() => { adminListUsers().then((r) => setUsers(r.users)).catch(() => undefined); }, []);

  if (limitHit) return <div className="fs-card pad st-col"><PlanLimitBlock body={limitHit} /></div>;

  const exportHref = auditExportUrl(filters());

  return (
    <div className="st-stack">
      <div className="fs-page-head">
        <div className="text">
          <h1>Audit log</h1>
          <div className="fs-page-sub">
            Every change in this account, newest first: sign-ins, team changes, forms, connections,
            exports and billing. Kept for 400 days. Entries name people by email even after their login is gone.
          </div>
        </div>
        {/* The server streams the same filtered trail as a file (§4). A plain
            link: the session cookie rides along and the browser saves it. */}
        <a className="fs-btn primary" href={exportHref} download>Export CSV</a>
      </div>

      <div className="st-filters">
        <select value={actorId} onChange={(e) => setActorId(e.target.value)} aria-label="Filter by person">
          <option value="">Everyone</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
        <select value={action} onChange={(e) => setAction(e.target.value)} aria-label="Filter by action">
          {ACTION_FILTERS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </div>
      {error && <div className="ad-err">{error}</div>}

      <TableCard
        footer={nextBefore !== null ? (
          <button className="fs-btn sm" disabled={busy} onClick={() => void load(nextBefore)}>
            {busy ? 'Loading…' : 'Load more'}
          </button>
        ) : undefined}
      >
        {!entries ? (
          <div className="st-card-body st-muted">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="st-card-body st-muted">Nothing recorded yet{action || actorId ? ' for this filter' : ''}.</div>
        ) : phone ? (
          <div className="ic-cards">
            {entries.map((e) => (
              <div key={e.id} className="ic-card">
                <div><b>{e.actor_email}</b> {describeAccountAudit(e)}</div>
                <div className="facts">{when(e.created_at)}</div>
              </div>
            ))}
          </div>
        ) : (
          <table className="fs-table">
            <thead><tr><th style={{ width: 170 }}>When</th><th style={{ width: 220 }}>Who</th><th>What</th></tr></thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td style={{ whiteSpace: 'nowrap' }} className="st-muted">{when(e.created_at)}</td>
                  <td style={{ fontWeight: 500 }}>{e.actor_email}</td>
                  <td>{describeAccountAudit(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableCard>
    </div>
  );
}
